import { BankSearchResult } from '../../../utils/types/candi_bank_types';
import { CandidateType } from '../constants/mockData';

/** Renders a candidate's parsed educations as one line. Entries are strings or objects
 * depending on how the CV parsed, so both shapes are handled. */
export function educationSummary(educations?: unknown[]): string | null {
  const parts = (educations || [])
    .map((edu: any) => {
      if (typeof edu === 'string') return edu;
      if (typeof edu === 'object' && edu !== null) {
        return [
          edu.degree || edu.title,
          edu.field_of_study || edu.field,
          edu.institution || edu.school || edu.university,
        ]
          .filter(Boolean)
          .join(' - ');
      }
      return null;
    })
    .filter(Boolean);
  return parts.length ? parts.join(' \u00b7 ') : null;
}

/**
 * Adapts a BankSearchResult to the CandidateType shape expected by
 * existing modals (CandidateProfileModal, AddToInviteListModal).
 */
export function bankResultToCandidateType(
  result: BankSearchResult,
): CandidateType {
  const firstName = result.firstName ?? '';
  const lastName = result.lastName ?? '';

  return {
    id: result.id,
    name: `${firstName} ${lastName}`.trim() || 'Unknown',
    avatar: '',
    skills: result.skills,
    educations: (result.educations as string[]) ?? [],
    work_experience: (result.workExperience as any[]) ?? [],
    score: result.score,
    status: result.jobSeekingStatus ?? '',
    experience: result.years,
    email: result.email ?? '',
    location: undefined,
    phone: undefined,
    inviteLists: [],
    filename: undefined,
    pdfUrl: undefined,
    documents: [],
  };
}

/**
 * Formats a Unix-seconds timestamp into a human-readable relative time string.
 * e.g. "just now", "3 min ago", "2 hours ago", "5 days ago".
 */
export function formatRelativeTime(unixSeconds: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - unixSeconds;

  if (diff < 60) return 'just now';
  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} min${mins !== 1 ? 's' : ''} ago`;
  }
  if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }
  if (diff < 2592000) {
    const days = Math.floor(diff / 86400);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }
  const months = Math.floor(diff / 2592000);
  return `${months} month${months !== 1 ? 's' : ''} ago`;
}

/**
 * Returns a source badge label for display.
 */
export function sourceLabel(source: string): string {
  switch (source) {
    case 'file':
      return 'Upload';
    case 'text':
      return 'Pasted';
    case 'opening':
      return 'Opening';
    default:
      return source;
  }
}

/**
 * Returns Tailwind classes for the source badge.
 */
export function sourceBadgeClasses(source: string): string {
  switch (source) {
    case 'file':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'text':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'opening':
      return 'bg-teal-50 text-teal-700 border-teal-200';
    default:
      return 'bg-slate-50 text-slate-600 border-slate-200';
  }
}

/**
 * Returns Tailwind classes for the status pill.
 */
export function statusPillClasses(status: string): string {
  switch (status) {
    case 'Completed':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'Processing':
    case 'In Queue':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'Error':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-slate-50 text-slate-600 border-slate-200';
  }
}
